import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import {
  TRACKER_EVIDENCE_COLLECTION,
  TRACKER_SOURCE_TYPES,
  TRACKER_STATUSES,
  TRACKERS_COLLECTION,
  buildTrackerPayload,
} from './trackerModel';
import { TRACKER_TYPES } from './trackerTypes';

const DAILY_LOGS_COLLECTION = 'dailyLogs';
const MAX_BATCH_WRITES = 400;
const normalizeText = (value = '') => String(value || '').trim();
const getCurrentUserId = () => auth.currentUser?.uid || '';

export const buildProfileGoalTrackerId = (goalId = '') => {
  const cleanGoalId = normalizeText(goalId);
  if (!cleanGoalId) throw new Error('Missing goal id.');
  return `profile-goal-${cleanGoalId.split('/').join('%2F')}`;
};

const getTrackerStatusForGoal = (goal = {}) => {
  const status = normalizeText(goal.status || 'active').toLowerCase();
  if (status === 'active') return TRACKER_STATUSES.ACTIVE;
  if (status === 'paused') return TRACKER_STATUSES.PAUSED;
  return TRACKER_STATUSES.ARCHIVED;
};

const isActiveGoalRelationship = (goal = {}) => (
  !['completed', 'archived', 'deleted'].includes(
    normalizeText(goal.status || 'active').toLowerCase()
  )
);

const readChildTrackers = async (childId, deps = {}) => {
  const firestore = deps.db || db;
  const collectionFn = deps.collection || collection;
  const getDocuments = deps.getDocs || getDocs;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const snapshot = await getDocuments(queryFn(
    collectionFn(firestore, TRACKERS_COLLECTION),
    whereFn('childId', '==', childId)
  ));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

const updateHistoricalGoalRelationship = async ({
  childId,
  goalId,
  active,
  deps = {},
}) => {
  const firestore = deps.db || db;
  const collectionFn = deps.collection || collection;
  const docFn = deps.doc || doc;
  const getDocuments = deps.getDocs || getDocs;
  const queryFn = deps.query || query;
  const whereFn = deps.where || where;
  const batchFn = deps.writeBatch || writeBatch;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const collections = [TRACKER_EVIDENCE_COLLECTION, DAILY_LOGS_COLLECTION];
  const snapshots = await Promise.all(collections.map((collectionName) => getDocuments(queryFn(
    collectionFn(firestore, collectionName),
    whereFn('childId', '==', childId)
  ))));
  const matchingDocs = snapshots.flatMap((snapshot, index) => snapshot.docs
    .filter((item) => {
      const data = item.data();
      return normalizeText(data.goalId || data.source?.goalId || data.metadata?.trackerEvidence?.goalId) === goalId;
    })
    .map((item) => ({ collectionName: collections[index], id: item.id })));

  for (let start = 0; start < matchingDocs.length; start += MAX_BATCH_WRITES) {
    const batch = batchFn(firestore);
    matchingDocs.slice(start, start + MAX_BATCH_WRITES).forEach(({ collectionName, id }) => {
      const isEvidence = collectionName === TRACKER_EVIDENCE_COLLECTION;
      batch.update(docFn(firestore, collectionName, id), {
        goalRelationshipActive: active,
        ...(!isEvidence ? {
          'source.goalRelationshipActive': active,
          'metadata.trackerEvidence.goalRelationshipActive': active,
        } : {}),
        updatedAt: timestamp(),
      });
    });
    await batch.commit();
  }
};

export const syncProfileGoalTracker = async ({
  childId = '',
  childName = '',
  goal = {},
  relationshipActive,
  deps = {},
} = {}) => {
  const cleanChildId = normalizeText(childId);
  const goalId = normalizeText(goal.id);
  if (!cleanChildId || !goalId || !normalizeText(goal.title)) {
    throw new Error('A child id, goal id, and goal title are required.');
  }

  const firestore = deps.db || db;
  const docFn = deps.doc || doc;
  const getDocument = deps.getDoc || getDoc;
  const setDocument = deps.setDoc || setDoc;
  const updateDocument = deps.updateDoc || updateDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const userId = deps.userId || getCurrentUserId();
  const trackerId = buildProfileGoalTrackerId(goalId);
  const trackerRef = docFn(firestore, TRACKERS_COLLECTION, trackerId);
  const existingSnapshot = await getDocument(trackerRef);
  const existing = existingSnapshot?.exists?.() ? existingSnapshot.data() : null;
  const activeRelationship = typeof relationshipActive === 'boolean'
    ? relationshipActive
    : isActiveGoalRelationship(goal);
  const now = timestamp();
  const payload = buildTrackerPayload({
    childId: cleanChildId,
    type: TRACKER_TYPES.GOAL,
    title: goal.title,
    description: goal.description,
    status: activeRelationship ? getTrackerStatusForGoal(goal) : TRACKER_STATUSES.ARCHIVED,
    source: {
      type: TRACKER_SOURCE_TYPES.PROFILE_GOAL,
      id: goalId,
      goalId,
      relationshipActive: activeRelationship,
      path: `children/${cleanChildId}.supportProfile.careGoals`,
    },
    createdBy: existing?.createdBy || userId,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  });

  await setDocument(trackerRef, payload, { merge: true });

  const allTrackers = await readChildTrackers(cleanChildId, deps);
  const duplicates = allTrackers.filter((tracker) => (
    tracker.id !== trackerId
    && tracker.source?.type === TRACKER_SOURCE_TYPES.PROFILE_GOAL
    && normalizeText(tracker.source?.goalId || tracker.source?.id) === goalId
    && tracker.status !== TRACKER_STATUSES.ARCHIVED
  ));
  await Promise.all(duplicates.map((tracker) => updateDocument(
    docFn(firestore, TRACKERS_COLLECTION, tracker.id),
    {
      status: TRACKER_STATUSES.ARCHIVED,
      duplicateOf: trackerId,
      updatedAt: timestamp(),
      archivedAt: timestamp(),
      archivedBy: userId,
    }
  )));

  if (!activeRelationship) {
    await updateHistoricalGoalRelationship({
      childId: cleanChildId,
      goalId,
      active: false,
      deps,
    });
  }

  return {
    id: trackerId,
    childName: normalizeText(childName),
    ...payload,
  };
};

export const syncProfileGoalTrackersForChild = async ({
  childId = '',
  childName = '',
  goals = [],
  deps = {},
} = {}) => {
  const cleanChildId = normalizeText(childId);
  if (!cleanChildId) throw new Error('Missing child id.');
  const currentGoals = (Array.isArray(goals) ? goals : [])
    .filter((goal) => normalizeText(goal.id) && normalizeText(goal.title));
  const currentGoalIds = new Set(currentGoals.map((goal) => normalizeText(goal.id)));
  const existingTrackers = await readChildTrackers(cleanChildId, deps);
  const removedGoalTrackers = existingTrackers.filter((tracker) => (
    tracker.source?.type === TRACKER_SOURCE_TYPES.PROFILE_GOAL
    && !currentGoalIds.has(normalizeText(tracker.source?.goalId || tracker.source?.id))
    && tracker.source?.relationshipActive !== false
  ));

  await Promise.all(currentGoals.map((goal) => syncProfileGoalTracker({
    childId: cleanChildId,
    childName,
    goal,
    deps,
  })));
  await Promise.all(removedGoalTrackers.map((tracker) => syncProfileGoalTracker({
    childId: cleanChildId,
    childName,
    goal: {
      id: tracker.source.goalId || tracker.source.id,
      title: tracker.title,
      description: tracker.description,
      status: 'deleted',
    },
    relationshipActive: false,
    deps,
  })));

  return currentGoals.map((goal) => buildProfileGoalTrackerId(goal.id));
};
